import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import type { Meditation, MeditationCategory, MeditationHistoryItem } from '@/types/meditation';

/**
 * Загружает все категории медитаций
 */
export function useMeditationCategories() {
  const [categories, setCategories] = useState<MeditationCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('meditation_categories')
        .select('*')
        .order('display_order');
      
      if (error) console.error('[meditations] categories error:', error);
      if (data) setCategories(data as MeditationCategory[]);
      setLoading(false);
    })();
  }, []);

  return { categories, loading };
}

/**
 * Загружает все треки (по желанию — с фильтром по категории)
 */
export function useMeditations(categorySlug?: string) {
  const [meditations, setMeditations] = useState<Meditation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      let query = supabase
        .from('meditations')
        .select('*, category:meditation_categories(*)')
        .order('display_order');
      
      if (categorySlug) {
        const { data: cat } = await supabase
          .from('meditation_categories')
          .select('id')
          .eq('slug', categorySlug)
          .maybeSingle();
        
        if (cat?.id) {
          query = query.eq('category_id', cat.id);
        }
      }

      const { data, error } = await query;
      if (error) console.error('[meditations] error:', error);
      if (data) setMeditations(data as Meditation[]);
      setLoading(false);
    })();
  }, [categorySlug]);

  return { meditations, loading };
}

/**
 * Один трек по slug
 */
export function useMeditation(slug: string | undefined) {
  const [meditation, setMeditation] = useState<Meditation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('meditations')
        .select('*, category:meditation_categories(*)')
        .eq('slug', slug)
        .maybeSingle();
      
      if (data) setMeditation(data as Meditation);
      setLoading(false);
    })();
  }, [slug]);

  return { meditation, loading };
}

/**
 * История прослушиваний — для секции «Продолжить»
 * Возвращает незавершённые треки (с прогрессом)
 */
export function useUnfinishedMeditations() {
  const { profile } = useUserStore();
  const [items, setItems] = useState<MeditationHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  async function refetch() {
    if (!profile?.id) return;
    
    const { data } = await supabase
      .from('meditation_history')
      .select('*, meditation:meditations(*, category:meditation_categories(*))')
      .eq('user_id', profile.id)
      .eq('completed', false)
      .gt('duration_listened_seconds', 5)
      .order('started_at', { ascending: false })
      .limit(3);
    
    if (data) setItems(data as MeditationHistoryItem[]);
    setLoading(false);
  }

  useEffect(() => {
    refetch();
  }, [profile?.id]);

  return { items, loading, refetch };
}

/**
 * Логирование начала прослушивания
 */
export async function logMeditationStart(userId: string, meditationId: string) {
  const { data } = await supabase
    .from('meditation_history')
    .insert({
      user_id: userId,
      meditation_id: meditationId,
      duration_listened_seconds: 0,
      completed: false,
    })
    .select()
    .maybeSingle();
  
  return data?.id as string | undefined;
}

/**
 * Обновление прогресса прослушивания
 */
export async function updateMeditationProgress(
  historyId: string, 
  seconds: number, 
  completed: boolean = false
) {
  await supabase
    .from('meditation_history')
    .update({
      duration_listened_seconds: Math.floor(seconds),
      completed,
    })
    .eq('id', historyId);
}
